import type { PurchaseOrder } from "@shared/types";
import type { ColumnDef } from "@tanstack/react-table";
import type { TFunction } from "i18next";
import { CROSS_BORDER_ERROR_STATUSES } from "./cross-border-constants";
import { CrossBorderTimeline } from "./cross-border-timeline";

const STATUS_CELL_CLASSES = {
  base: "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium",
  error: "bg-destructive/10 text-destructive",
  delivered: "bg-green-100 text-green-700",
  shipped: "bg-blue-100 text-blue-700",
  default: "bg-muted text-muted-foreground",
} as const;

/**
 * Returns the cross-border shipping direction label (e.g. "DE → NL").
 * Extracts the 2-letter origin country from the vendor key prefix and compares
 * it to the shipping address country code. Returns "—" when not cross-border.
 */
const getDirection = (order: PurchaseOrder): string => {
  // Use the direction resolved by the backend from the vendor's real ship-from
  // country. Do NOT re-derive from the vendor region: "BDE" covers both NL- and
  // DE-warehoused roasters, so the region prefix misclassifies NL origins.
  const direction = order.crossBorderDirection;
  if (!direction) return "—";
  return direction.replace("_TO_", " → ");
};

/**
 * Returns the carrier name from the first line item that has one,
 * falling back to the provided pending label when none is found.
 */
const getCarrier = (order: PurchaseOrder, pendingLabel: string): string => {
  for (const lineItem of order.lineItems) {
    if (lineItem.carrier) return lineItem.carrier;
  }
  return pendingLabel;
};

/**
 * Returns the tracking number and optional URL from the first line item
 * that has a tracking number, or null if none is found.
 */
const getTrackingNumber = (order: PurchaseOrder): { number: string; url?: string } | null => {
  for (const lineItem of order.lineItems) {
    if (lineItem.trackingNumber) {
      return { number: lineItem.trackingNumber, url: lineItem.trackingUrl };
    }
  }
  return null;
};

export const CROSS_BORDER_COLUMN_IDS = {
  ORDER_ID: "purchaseOrderId",
  DIRECTION: "direction",
  STATUS: "orderStatus",
  CARRIER: "carrier",
  TRACKING: "tracking",
  CREATED_AT: "createdAt",
  VENDOR: "vendorKey",
  TIMELINE: "timeline",
} as const;

export const getCrossBorderColumns = (t: TFunction): ColumnDef<PurchaseOrder>[] => {
  const pendingLabel = t("admin.crossBorder.columns.pending");

  return [
    {
      id: CROSS_BORDER_COLUMN_IDS.ORDER_ID,
      accessorKey: "purchaseOrderId",
      header: t("admin.crossBorder.columns.orderId"),
      cell: ({ row }) => <span className="font-mono text-sm">{row.original.purchaseOrderId}</span>,
    },
    {
      id: CROSS_BORDER_COLUMN_IDS.DIRECTION,
      header: t("admin.crossBorder.columns.direction"),
      accessorFn: getDirection,
      cell: ({ row }) => {
        const direction = getDirection(row.original);
        return <span className="whitespace-nowrap text-sm font-medium">{direction}</span>;
      },
    },
    {
      id: CROSS_BORDER_COLUMN_IDS.STATUS,
      accessorKey: "orderStatus",
      header: t("admin.crossBorder.columns.status"),
      cell: ({ row }) => {
        const status = row.original.orderStatus;
        const isError = status && CROSS_BORDER_ERROR_STATUSES.has(status);
        let statusClass: string = STATUS_CELL_CLASSES.default;
        if (isError) statusClass = STATUS_CELL_CLASSES.error;
        else if (status === "Delivered") statusClass = STATUS_CELL_CLASSES.delivered;
        else if (status === "Shipped") statusClass = STATUS_CELL_CLASSES.shipped;
        return <span className={`${STATUS_CELL_CLASSES.base} ${statusClass}`}>{status ?? "—"}</span>;
      },
    },
    {
      id: CROSS_BORDER_COLUMN_IDS.CARRIER,
      header: t("admin.crossBorder.columns.carrier"),
      accessorFn: (row) => getCarrier(row, pendingLabel),
      cell: ({ row }) => <span className="text-sm">{getCarrier(row.original, pendingLabel)}</span>,
    },
    {
      id: CROSS_BORDER_COLUMN_IDS.TRACKING,
      header: t("admin.crossBorder.columns.tracking"),
      cell: ({ row }) => {
        const tracking = getTrackingNumber(row.original);
        if (!tracking) {
          return <span className="text-sm text-muted-foreground">—</span>;
        }
        return tracking.url ? (
          <a
            href={tracking.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-blue-600 hover:underline"
            data-testid="tracking-link"
          >
            {tracking.number}
          </a>
        ) : (
          <span className="font-mono text-sm">{tracking.number}</span>
        );
      },
    },
    {
      id: CROSS_BORDER_COLUMN_IDS.CREATED_AT,
      accessorKey: "createdAt",
      header: t("admin.crossBorder.columns.created"),
      cell: ({ row }) => <span className="text-sm text-muted-foreground">{new Date(row.original.createdAt).toLocaleDateString()}</span>,
    },
    {
      id: CROSS_BORDER_COLUMN_IDS.VENDOR,
      accessorKey: "vendorKey",
      header: t("admin.crossBorder.columns.vendor"),
      cell: ({ row }) => <span className="text-sm">{row.original.vendorName ?? row.original.vendorKey}</span>,
    },
    {
      id: CROSS_BORDER_COLUMN_IDS.TIMELINE,
      header: t("admin.crossBorder.columns.timeline"),
      cell: ({ row }) => <CrossBorderTimeline status={row.original.orderStatus} />,
    },
  ];
};
